/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */
import filter from 'lodash/fp/filter.js';
import get from 'lodash/fp/get.js';
import getOr from 'lodash/fp/getOr.js';
import groupBy from 'lodash/fp/groupBy.js';
import map from 'lodash/fp/map.js';
import startCase from 'lodash/fp/startCase.js';
import uniq from 'lodash/fp/uniq.js';
import { keys } from 'ramda';
import {
  getFormInitialValues,
  getFormMeta,
  getFormSyncErrors,
  getFormValues,
  formValueSelector,
} from 'redux-form';
import { createSelector } from 'reselect';
import {
  COMPONENT_ID,
  DEFAULT_EDP,
  GROUP,
  NAME,
} from '@hbrisk/sp3-risk-model-support/components/app/attributes/names/index.js';
import { rootComponentQuestions } from '@hbrisk/sp3-risk-model-support/models/componentQuestions/index.js';
import { formatLocationSpecifierFromRangeArray } from '@hbrisk/sp3-risk-model-support/models/locationSpecifier/index.js';
import {
  BUILDING_TYPE_DIR_1,
  BUILDING_TYPE_DIR_2,
  COMPONENT_POPULATION,
  COMPONENT_POPULATION_COMPONENT_UUID as COMPONENT_UUID,
  COMPONENT_POPULATION_METHOD,
  COMPONENT_POPULATION_PERFORMANCE_GROUPS as PERFORMANCE_GROUPS,
  COUNTRY,
  LAT,
  LNG,
  NUMBER_OF_STORIES,
  PERFORMANCE_GROUP_RESPONSE_ITEM_KEY as RESPONSE_ITEM_KEY,
  REPAIR_TIME_METHOD,
  RESIDUAL_DRIFT_DISPERSION_DIR_1,
  RESIDUAL_DRIFT_DISPERSION_DIR_2,
  RESIDUAL_DRIFT_DISTRIBUTION_MODEL_DIR_1,
  RESIDUAL_DRIFT_DISTRIBUTION_MODEL_DIR_2,
  RESIDUAL_DRIFT_MODEL_DIR_1,
  RESIDUAL_DRIFT_MODEL_DIR_2,
  STRUCTURAL_RESPONSES,
  STRUCTURAL_RESPONSE_DERIVED_FROM,
  NUMBER_OF_SUBGRADE_STORIES,
} from '@hbrisk/sp3-risk-model-support/models/app/attributes/names/index.js';
import {
  COMPONENT_POPULATION_METHOD_VALUES,
  REPAIR_TIME_METHOD_VALUES,
} from '@hbrisk/sp3-risk-model-support/models/app/attributes/values/index.js';
import { EDP_RIDR } from '@hbrisk/sp3-risk-model-support/edps/edps.js';
import getAvailableEditionsForLocation from '@hbrisk/sp3-risk-model-support/models/hazardEditions/getAvailableEditionsForLocation.js';
import { edpsByKey } from '#constants/edps/index.js';
import { selectComponentsByUuid } from '#selectors/entities/components.js';
import createDeepEqualSelector from '#selectors/support/createDeepEqualSelector.js';
import countFieldArrayItemErrors from '#support/utility/form/validation/countFieldArrayItemErrors/index.js';
import componentRollup from '#support/components/componentRollup.js';
import getTouchedFromMeta from '#support/utility/form/getTouchedFromMeta/index.js';
import isEdpFloor from '#support/models/isFloor.js';
import { methodById } from '#constants/models/structuralResponseMethods/structuralResponseMethods.js';
import isInvisible from '#selectors/ui/form/support/isVisible.js';
import makeSelectAutofilled from '#selectors/ui/form/support/makeSelectAutofilled.js';
import makeSelectVisibleFields from '#selectors/ui/form/support/makeSelectVisibleFields.js';
import requiredEdpsForBuildingTypes from '#support/models/buildingType/requiredEdpsForBuildingTypes/index.js';

/**
 * Note: All exports on this page should be curried and have a first parameter of form
 */

const { CUSTOM } = COMPONENT_POPULATION_METHOD_VALUES;

const isVisible = (field, visibleFields) => !!get(field, visibleFields);

export const selectInvisibleFields = form =>
  createSelector(
    state => getOr({}, `form[${form}].visibleFields`, state),
    visibleFields => keys(visibleFields).filter(field => isInvisible(field, visibleFields))
  );

export const selectVisibleFields = form =>
  createSelector(
    state => getOr({}, `form[${form}].visibleFields`, state),
    visibleFields => keys(visibleFields).filter(field => isVisible(field, visibleFields))
  );

export const selectVisibleRootComponentQuestions = form =>
  createSelector(selectVisibleFields(form), visibleFields =>
    rootComponentQuestions.filter(question => visibleFields.includes(question.name))
  );

const selectVisibleRootComponentQuestionsByGroup = form =>
  createSelector(selectVisibleRootComponentQuestions(form), rootVisibleQuestions =>
    groupBy('group', rootVisibleQuestions)
  );

export const selectVisibleRootComponentQuestionSubGroupsByGroup = form =>
  createSelector(
    selectVisibleRootComponentQuestionsByGroup(form),
    questionsByGroup => group => uniq(map(q => q.subGroup, questionsByGroup[group]))
  );

export const selectVisibleRootComponentQuestionsByGroupSubGroup = form =>
  createSelector(
    selectVisibleRootComponentQuestionsByGroup(form),
    questionsByGroup => (group, subGroup) =>
      filter(q => q.subGroup === subGroup, questionsByGroup[group])
  );

const selectName = (_, props) => props.name;

const selectPath = (_, props) => props.path;

const selectResponseItemKey = (_, props) => props.responseItemKey;

const selectEdpKey = (_, props) => props.edpKey;

export const selectFieldsVisibility = makeSelectVisibleFields;

export const selectFormFieldVisibility = form =>
  createSelector(
    selectFieldsVisibility(form),
    visibleFields => field => isVisible(field, visibleFields)
  );

export const makeFormFieldVisibilitySelector = form => () =>
  createSelector(selectFormFieldVisibility(form), visibleFields => field => visibleFields(field));

export const selectFormInitialized = form =>
  createSelector(
    state => getFormInitialValues(form)(state),
    initial => initial !== undefined
  );

export const selectAutofilledFormFields = makeSelectAutofilled;

export const makeFieldIsAutofilledSelector = form => () =>
  createSelector(selectAutofilledFormFields(form), selectName, (autofilled, name) =>
    get(name, autofilled)
  );

export const selectIsAutofilledFormField = form => makeFieldIsAutofilledSelector(form)();

export const selectTouchedFields = form =>
  createDeepEqualSelector(getFormMeta(form), getTouchedFromMeta);

export const selectAllFormValues = form => getFormValues(form);

export const selectFormValues = form => formValueSelector(form);

export const selectModelComponentTableData = form =>
  createDeepEqualSelector(
    state =>
      formValueSelector(form)(
        state,
        COMPONENT_POPULATION,
        NUMBER_OF_STORIES,
        NUMBER_OF_SUBGRADE_STORIES
      ),
    getFormSyncErrors(form),
    getFormMeta(form),
    selectComponentsByUuid,
    (formValues, errors, meta, componentsById) => {
      const {
        [COMPONENT_POPULATION]: componentPopulation,
        [NUMBER_OF_STORIES]: numberOfStoriesStr,
        [NUMBER_OF_SUBGRADE_STORIES]: numberOfSubgradeStoriesStr,
      } = formValues;
      const numberOfStories = parseInt(numberOfStoriesStr, 10);
      const numberOfSubgradeStories = parseInt(numberOfSubgradeStoriesStr, 10);
      if (
        !componentPopulation ||
        componentPopulation.some(popItem => {
          const { componentUuid } = popItem;
          return !componentsById[componentUuid];
        })
      ) {
        return [];
      }
      return componentPopulation
        .map((populationItem, index) => {
          const { [COMPONENT_UUID]: componentUuid, [PERFORMANCE_GROUPS]: performanceGroups } =
            populationItem;
          const {
            [COMPONENT_ID]: componentId,
            [NAME]: name,
            [GROUP]: group,
            [DEFAULT_EDP]: defaultEdpKey,
          } = componentsById[componentUuid];
          const locationType = get(`${defaultEdpKey}.locationType`, edpsByKey);
          const isFloor = isEdpFloor(locationType);
          const rollups = performanceGroups.reduce(
            (acc, curr) => {
              const { quantity, locations, error } = componentRollup(
                isFloor,
                numberOfStories,
                numberOfSubgradeStories,
                curr
              );
              return {
                error: acc.error || error,
                quantity: acc.quantity + quantity,
                locations: [...acc.locations, ...locations],
              };
            },
            { quantity: 0, locations: [] }
          );
          const locations = formatLocationSpecifierFromRangeArray(
            isFloor,
            numberOfStories,
            numberOfSubgradeStories,
            uniq(rollups.locations).sort((a, b) => a - b)
          );
          const errorCount = countFieldArrayItemErrors(COMPONENT_POPULATION, index, errors, meta);
          return {
            index,
            componentUuid,
            componentId,
            name,
            group: startCase(group),
            locations,
            quantity: rollups.quantity,
            error: rollups.error,
            errorCount,
          };
        })
        .sort((a, b) => {
          const componentIdComparison = a[COMPONENT_ID].localeCompare(b[COMPONENT_ID]);
          if (componentIdComparison !== 0) {
            return componentIdComparison;
          }
          return a[NAME].localeCompare(b[NAME]);
        });
    }
  );

export const selectResponseItemKeysWithDefinedComponents = form =>
  createSelector(
    state => formValueSelector(form)(state, COMPONENT_POPULATION, COMPONENT_POPULATION_METHOD),
    formValues => {
      const {
        [COMPONENT_POPULATION]: componentPopulation,
        [COMPONENT_POPULATION_METHOD]: componentPopulationMethod,
      } = formValues;

      if (componentPopulationMethod === CUSTOM) {
        const res = componentPopulation.reduce((acc, component) => {
          const { [PERFORMANCE_GROUPS]: performanceGroups } = component;
          performanceGroups.forEach(group => {
            const { [RESPONSE_ITEM_KEY]: responseItemKey } = group;
            if (responseItemKey) {
              acc[responseItemKey] = 1;
            }
          });
          return acc;
        }, {});
        return keys(res);
      }
      return [];
    }
  );

export const selectFormSyncErrors = form =>
  createSelector(getFormSyncErrors(form), selectPath, (errors, path) => get(path, errors));

const selectBuildingTypeIds = form => state =>
  formValueSelector(form)(state, BUILDING_TYPE_DIR_1, BUILDING_TYPE_DIR_2);

const selectRequiredStructuralResponses = form =>
  createSelector(
    selectBuildingTypeIds(form),
    ({ [BUILDING_TYPE_DIR_1]: buildingTypeDir1, [BUILDING_TYPE_DIR_2]: buildingTypeDir2 }) =>
      requiredEdpsForBuildingTypes(buildingTypeDir1, buildingTypeDir2)
  );

const selectStructuralResponses = form => state =>
  formValueSelector(form)(state, STRUCTURAL_RESPONSES) || [];

export const selectStructuralResponseTableData = form =>
  createSelector(
    selectStructuralResponses(form),
    selectRequiredStructuralResponses(form),
    selectResponseItemKeysWithDefinedComponents(form),
    getFormSyncErrors(form),
    getFormMeta(form),
    (responses, requiredResponses, responseKeysWithComponents, errors, meta) => {
      if (!Array.isArray(responses)) {
        return [];
      }

      return responses.map((response, index) => {
        const { edpKey, method } = response;
        const errorCount =
          edpKey === EDP_RIDR
            ? countFieldArrayItemErrors(STRUCTURAL_RESPONSES, index, errors, meta) +
              [
                RESIDUAL_DRIFT_DISPERSION_DIR_1,
                RESIDUAL_DRIFT_DISPERSION_DIR_2,
                RESIDUAL_DRIFT_DISTRIBUTION_MODEL_DIR_1,
                RESIDUAL_DRIFT_DISTRIBUTION_MODEL_DIR_2,
                RESIDUAL_DRIFT_MODEL_DIR_1,
                RESIDUAL_DRIFT_MODEL_DIR_2,
              ].filter(name => errors[name] && getTouchedFromMeta(meta)[name]).length
            : countFieldArrayItemErrors(STRUCTURAL_RESPONSES, index, errors, meta);
        const { name: edpName, abbreviation, locationType, edpUnit } = edpsByKey[edpKey];

        const derivedFromName = responses.find(
          r => r.itemKey === response[STRUCTURAL_RESPONSE_DERIVED_FROM]
        )?.name;

        const hasMultipleResponsesForEdp = responses.filter(r => r.edpKey === edpKey).length > 1;

        return {
          ...response,
          derivedFromName,
          responseIndex: index,
          edpName,
          abbreviation,
          methodDescription: method ? methodById[method].abbreviation : null,
          locationType,
          edpUnit,
          errorCount,
          exceedsRequirement: !requiredResponses.includes(edpKey) || hasMultipleResponsesForEdp,
          hasComponents: responseKeysWithComponents.includes(response.itemKey),
        };
      });
    }
  );

export const selectStructuralResponsesContainExceedsRequirement = form =>
  createSelector(selectStructuralResponseTableData(form), structuralResponseTableData =>
    structuralResponseTableData.some(response => response.exceedsRequirement)
  );

export const selectEdpKeys = form =>
  createSelector(selectStructuralResponses(form), responses =>
    responses.map(response => response.edpKey)
  );

export const selectResponsesForEdp = form =>
  createSelector(selectStructuralResponses(form), selectEdpKey, (responses, edpKey) =>
    responses.filter(response => response.edpKey === edpKey)
  );

export const isResponseCountGreaterThanMaxForEdp = form =>
  createSelector(selectResponsesForEdp(form), responses => responses.length > 10);

export const selectResponseByKey = form =>
  createSelector(selectStructuralResponses(form), selectResponseItemKey, (responses, key) =>
    responses.find(response => response.itemKey === key)
  );

export const selectRepairTimeMethodSupportsServiceLocations = form =>
  createSelector(
    state => formValueSelector(form)(state, REPAIR_TIME_METHOD),
    method => method === REPAIR_TIME_METHOD_VALUES.ATC_138
  );

export const selectCountry = form => state => formValueSelector(form)(state, COUNTRY);

export const selectIsCountryUS = form =>
  createSelector(selectCountry(form), country => country === 'US');

export const selectIsCountryJapan = form =>
  createSelector(selectCountry(form), country => country === 'JP');

export const selectAvailableEditions = form =>
  createSelector(
    state => formValueSelector(form)(state, LAT, LNG),
    ({ [LAT]: lat, [LNG]: lng }) => getAvailableEditionsForLocation(lat, lng)
  );
